import React from 'react';
import Card from '../../components/ui/Card.jsx';
import Button from '../../components/ui/Button.jsx';
import { apiFetch } from '../../api.js';
import { useApi } from '../../hooks/useApi.js';
import { RouteContext } from '../../router.jsx';

export default function StaffStudentDelete({ params }) {
  const { navigate } = React.useContext(RouteContext);
  const studentId = params?.id;
  const { data, loading, error } = useApi(`/api/students/${studentId}`, [studentId]);
  const [message, setMessage] = React.useState({ text: '', tone: 'info' });
  const [saving, setSaving] = React.useState(false);

  if (!studentId) {
    return (
      <div className="page">
        <div className="alert error">Missing student id.</div>
      </div>
    );
  }

  const fullName = data ? `${data.first_name || ''} ${data.last_name || ''}`.trim() : '';

  const handleDelete = async () => {
    if (saving) return;
    setSaving(true);
    setMessage({ text: '', tone: 'info' });
    try {
      await apiFetch(`/api/students/${studentId}`, { method: 'DELETE' });
      navigate('/staff/students');
    } catch (err) {
      setMessage({ text: err?.message || 'Failed to delete student.', tone: 'error' });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <h1>Delete Student</h1>
          <p className="subtle">This action permanently removes the student record.</p>
        </div>
        <Button variant="secondary" type="button" onClick={() => navigate(`/staff/students/${studentId}/edit`)}>
          Back to Edit
        </Button>
      </div>

      {error ? <div className="alert error">{error}</div> : null}
      {message.text ? <div className={message.tone === 'error' ? 'alert error' : 'alert'}>{message.text}</div> : null}

      {loading || !data ? (
        <p className="subtle">Loading...</p>
      ) : (
        <Card title="Confirm Deletion">
          <div className="summary-list">
            <div className="summary-row">
              <span>Name</span>
              <span>{fullName || '—'}</span>
            </div>
            <div className="summary-row">
              <span>CIN</span>
              <span>{data.cin || '—'}</span>
            </div>
            <div className="summary-row">
              <span>Campus Username</span>
              <span>{data.username || '—'}</span>
            </div>
            <div className="summary-row">
              <span>Personal Email</span>
              <span>{data.personal_email || '—'}</span>
            </div>
          </div>
          <p className="subtle mt-3">
            Deleting this student removes the record from the system and cannot be undone.
          </p>
          <div className="form-actions">
            <Button
              variant="secondary"
              type="button"
              onClick={() => navigate(`/staff/students/${studentId}/edit`)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button type="button" onClick={handleDelete} disabled={saving}>
              {saving ? 'Deleting...' : 'Delete Student'}
            </Button>
          </div>
        </Card>
      )}
    </div>
  );
}
